import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Dealer } from '../interfaces/waybill.interfaces';
import { Pallets } from '../interfaces/Pallets.interfaces';
import { SysReturnList } from '../interfaces/SysReturnList.interfaces';
import { AuthService } from './auth.service';

/**
 * Cliente + brand abilitati per l'utente (estratti da userFunctions).
 * Il backend return-request si aspetta la chiave `clientBrands` nel body
 * (a differenza di shipment-management che usa `clientsBrands`).
 */
export interface ClientBrandsEntry {
  clientCode?: string;
  clientName?: string;
  brands?: { brandCode?: string; brandName?: string }[];
}

/** Cliente restituito da /getClientsAndBrands */
export interface ClientInfo {
  clientCode: string;
  clientName?: string | null;
  clientAcronym?: string | null;
  clientStatus?: string | null;
  clientVatNumber?: string | null;
}

/** Brand restituito da /getClientsAndBrands */
export interface BrandInfo {
  brandCode: string;
  brandName?: string | null;
  brandAcronym?: string | null;
  brandStatus?: string | null;
  brandDescription?: string | null;
}

/** Mapping clientCode -> array di brandCode */
export type ClientBrandsMap = { [clientCode: string]: string[] };

/** Risposta completa di /getClientsAndBrands */
export interface ClientsAndBrandsResponse {
  clients: ClientInfo[];
  brands: BrandInfo[];
  clientBrands: ClientBrandsMap;
}

/**
 * Payload per la generazione server-side di N pallet (modalità 'generate').
 */
export interface GeneratePalletsRequest {
  dealerCode: string;
  quantity: number;
  params: Record<string, any>;
}

/** Chiavi che identificano il pallet da eliminare (body di /delete) */
export interface DeletePalletKeys {
  palletCode: string;
  palletWaybillCode: string;
  palletDealerCode: string;
  palletShipmentCode: string;
}

/** Risposta di /delete */
export interface DeletePalletResponse {
  deleted: boolean;
  palletCode: string;
}

/**
 * Filtri della sezione "Filtri e Azioni" inviati a /getPalletsByDealerCodes.
 * Vengono valorizzate solo le chiavi effettivamente impostate dall'utente;
 * le date viaggiano in ISO 8601 (dateFrom a inizio giornata, dateTo a fine).
 */
export interface ReturnRequestFilters {
  palletCode?: string;
  palletWaybillCode?: string;
  dateFrom?: string;
  dateTo?: string;
}

/** Risposta paginata di /getPalletsByDealerCodes */
export interface GetPalletsByDealerCodesResponse {
  data: Pallets[];
  totalCount: number;
  totalPages: number;
  page: number;
  pageSize: number;
}

/**
 * Service per la gestione delle richieste di reso.
 * Mappato sul backend: /api/return-request
 *
 * Segue il pattern di ShipmentManagementService: ogni chiamata include
 * clientBrands nel body (per il permission check lato backend), gli header
 * con il token JWT e passa dalla gestione errori centralizzata.
 */
@Injectable({
  providedIn: 'root'
})
export class ReturnRequestService {
  private baseUrl = `${environment.apiUrl}/return-request`;
  private token = this.authService.getAccessToken();
  private language = localStorage.getItem('language');
  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.token}`,
      'Accept-language': this.language ?? 'it'
    })
  };

  constructor(private http: HttpClient, private authService: AuthService) {}

  /** Costruisce il body comune: campi specifici + clientBrands se presente */
  private buildRequestBody(clientBrands: ClientBrandsEntry[] | undefined, extra: Record<string, any>): any {
    const body: any = { ...extra };
    if (clientBrands && clientBrands.length) {
      body.clientBrands = clientBrands;
    }
    return body;
  }

  /**
   * Ritorna clienti, brand e mapping cliente -> brand da OPR_CLIENT_BRANDS.
   * Backend: POST /api/return-request/getClientsAndBrands
   * Body: { clientCodes? } — se omesso o vuoto restituisce tutti i clienti.
   */
  getClientsAndBrands(clientCodes?: string[]): Observable<ClientsAndBrandsResponse> {
    const body = clientCodes && clientCodes.length > 0 ? { clientCodes } : {};
    return this.http.post<ClientsAndBrandsResponse>(`${this.baseUrl}/getClientsAndBrands`, body, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Recupera la lista dei dealer corrispondenti ai codici inviati.
   * Se l'array è vuoto/omesso, restituisce un observable vuoto (nessuna chiamata).
   */
  getDealersByCodes(dealerCodes: string[], clientBrands?: ClientBrandsEntry[]): Observable<Dealer[]> {
    if (!dealerCodes || dealerCodes.length === 0) {
      return of([]);
    }
    const body = this.buildRequestBody(clientBrands, { dealerCodes });
    return this.http.post<Dealer[]>(`${this.baseUrl}/getDealersByCodes`, body, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Recupera i pallet filtrati per dealer codes con paginazione backend.
   * Backend: POST /api/return-request/getPalletsByDealerCodes
   * Body: { dealerCodes, clientBrands, page (1-based), pageSize, filters }
   * `filters` viene incluso solo se almeno un filtro è valorizzato: i codici
   * sono ricerche parziali, le date delimitano palletDate.
   */
  getPalletsByDealerCodes(
    dealerCodes: string[],
    clientBrands?: ClientBrandsEntry[],
    page: number = 1,
    pageSize: number = 100,
    filters?: ReturnRequestFilters
  ): Observable<GetPalletsByDealerCodesResponse> {
    const extra: Record<string, any> = { dealerCodes, page, pageSize };
    if (filters && Object.keys(filters).length) {
      extra['filters'] = filters;
    }
    const body = this.buildRequestBody(clientBrands, extra);
    return this.http.post<GetPalletsByDealerCodesResponse>(`${this.baseUrl}/getPalletsByDealerCodes`, body, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Ritorna i record SYS_RETURN_LIST per il dealer indicato: dal dealer il
   * backend ricava il client code e con quello filtra i record.
   * Backend: POST /api/return-request/getReturnListByDealerCode
   * Body: { returnDealerCode, clientBrands }
   */
  getReturnListByDealerCode(returnDealerCode: string, clientBrands?: ClientBrandsEntry[]): Observable<SysReturnList[]> {
    const body = this.buildRequestBody(clientBrands, { returnDealerCode });
    return this.http.post<SysReturnList[]>(`${this.baseUrl}/getReturnListByDealerCode`, body, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Modalità 'insert': il client invia in batch un array di entità Pallets gia costruite.
   * Backend atteso: POST /api/return-request/createPalletsBatch
   * Body: { pallets: Pallets[], clientBrands } -> Pallets[]
   */
  createPalletsBatch(pallets: Pallets[], clientBrands?: ClientBrandsEntry[]): Observable<Pallets[]> {
    if (!pallets || pallets.length === 0) {
      return of([]);
    }
    const body = this.buildRequestBody(clientBrands, { pallets });
    return this.http.post<Pallets[]>(`${this.baseUrl}/createPalletsBatch`, body, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Crea uno o più pallet in OPR_PALLETS (bottone "Genera reso", modalità 'generate').
   * Backend: POST /api/return-request/create
   * Body: { pallets: Opr_Pallets_DTO[], clientBrands } — clientBrands serve al
   * check permessi lato backend. Per ogni pallet il server imposta palletCode
   * (sequence), palletDate (oggi) e palletStatus (REQUEST_RETURN); il codice
   * bolla (palletWaybillCode) è unico e condiviso da tutti i pallet della richiesta.
   * Massimo 99 pallet per chiamata.
   * Risponde 201 con la lista dei pallet creati (palletId generati dal DB).
   */
  createPallet(pallets: Pallets[], clientBrands?: ClientBrandsEntry[]): Observable<Pallets[]> {
    if (!pallets || pallets.length === 0) {
      return of([]);
    }
    const body = this.buildRequestBody(clientBrands, { pallets });
    return this.http.post<Pallets[]>(`${this.baseUrl}/create`, body, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Elimina un pallet identificato dai quattro codici (palletCode,
   * palletWaybillCode, palletDealerCode, palletShipmentCode). Il backend
   * consente la cancellazione solo se palletDate cade nella giornata
   * odierna (altrimenti 409); 404 se il pallet non esiste.
   * Backend: POST /api/return-request/delete
   * Body: { ...DeletePalletKeys, clientBrands } -> { deleted, palletCode }
   */
  deletePallet(keys: DeletePalletKeys, clientBrands?: ClientBrandsEntry[]): Observable<DeletePalletResponse> {
    const body = this.buildRequestBody(clientBrands, { ...keys });
    return this.http.post<DeletePalletResponse>(`${this.baseUrl}/delete`, body, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Modalità 'generate': il backend genera N entità Pallets a partire dai parametri ricevuti.
   * Backend atteso: POST /api/return-request/generatePallets
   * Body: { dealerCode, quantity, params, clientBrands } -> Pallets[]
   */
  generatePallets(request: GeneratePalletsRequest, clientBrands?: ClientBrandsEntry[]): Observable<Pallets[]> {
    const body = this.buildRequestBody(clientBrands, { ...request });
    return this.http.post<Pallets[]>(`${this.baseUrl}/generatePallets`, body, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Stampa delle bolle di reso: il backend costruisce un unico PDF con tutte
   * le bolle richieste (i pallet che condividono lo stesso palletWaybillCode).
   * Backend: POST /api/return-request/waybills-print
   * Body: array dei codici bolla -> application/pdf (404 se nessun documento)
   */
  printWaybills(palletWaybillCodes: string[]): Observable<Blob> {
    const codes = (palletWaybillCodes || [])
      .map((code) => (code || '').trim())
      .filter((code) => !!code);
    return this.http.post(`${this.baseUrl}/waybills-print`, codes, {
      headers: this.httpOptions.headers,
      responseType: 'blob'
    }).pipe(
      catchError(this.handleError)
    );
  }

  // =============================================
  // ERROR HANDLING
  // =============================================

  /**
   * Gestione centralizzata degli errori (stesso schema di ShipmentManagementService).
   * Logga un messaggio leggibile e rilancia l'errore originale così i
   * componenti mantengono accesso a status/body nelle proprie error callback.
   */
  private handleError(error: any): Observable<never> {
    let errorMessage = 'Si è verificato un errore sconosciuto';
    if (error.error instanceof ErrorEvent) {
      // Errore lato client
      errorMessage = `Errore: ${error.error.message}`;
    } else {
      // Errore lato server
      switch (error.status) {
        case 400:
          errorMessage = 'Richiesta non valida';
          break;
        case 401:
          errorMessage = 'Non autorizzato';
          break;
        case 403:
          errorMessage = 'Accesso negato';
          break;
        case 404:
          errorMessage = 'Risorsa non trovata';
          break;
        case 422:
          errorMessage = error.error?.message || 'Dati non validi';
          break;
        case 500:
          errorMessage = 'Errore interno del server';
          break;
        default:
          errorMessage = `Errore ${error.status}: ${error.error?.message || error.statusText}`;
      }
    }

    console.error('Errore HTTP [return-request]:', errorMessage, error);
    return throwError(() => error);
  }
}
